// src/lib/scAvatar.js — AVATAR: componente único do design system.
// docs/DIRETRIZ-AVATARES.md · decisões de 13–14/07 (Rafael).
//
//   PESSOA → círculo,  2 iniciais   · trabalhador (capacete+ombros) · cliente (busto)
//   COISA  → quadrado, 1 letra      · obra (skyline) · empresa/fornecedor (sem arte)
//
// Pessoa tem rosto — e um dia tem foto. Coisa não tem. Antes era tudo círculo, e uma obra
// parecia um colega de trabalho. A arte é MARCA D'ÁGUA atrás da letra: em 24px vira textura,
// em 48px+ se lê. A letra leva contorno na cor do fundo (paint-order) e nunca se mistura.
//
// Espelha o Avatar do shadcn (Avatar > AvatarImage + AvatarFallback + AvatarBadge; e
// AvatarGroup + AvatarGroupCount): o fallback vive DENTRO do componente — nenhuma tela
// escreve `foto ? <img> : <iniciais>` de novo. Era assim que o card "Equipe em campo" e o
// Dashboard RH ignoravam o foto_url que eles mesmos já tinham carregado.
//
// ⚠️ `key` é SEMPRE o id da entidade. A cor sai de um hash da chave: se uma tela usa o id e
//    outra usa o nome, a MESMA obra ganha cores diferentes em telas diferentes — era o que
//    acontecia entre Obras e Contas a Pagar. Cor é identidade, não enfeite.
//
// PALETA (14/07): mesma família da rampa "Aço & Areia" — mesmos matizes (aço 250, teal 210,
// sálvia 155, areia 85, argila 45, terracota 20), com chroma de identidade (.11-.14) em vez
// de chroma de dado (.05-.09). A anterior era, literalmente, os hexes de --sc-ok/--sc-bad/
// --sc-warn/--sc-info: duas pessoas quaisquer recebiam "verde = pago" e "vermelho = vencido"
// como cor de rosto.

const PALETA = [
  ['#6DB6FF', '#0A253F'], // 1 · aco       (par do --chart-1)
  ['#00ADC4', '#002B33'], // 2 · teal      (par do --chart-2)
  ['#3FB171', '#012D16'], // 3 · salvia    (par do --chart-3)
  ['#C09000', '#322100'], // 4 · areia     (par do --chart-4)
  ['#C7734B', '#391A0C'], // 5 · argila    (par do --chart-5)
  ['#FF9A9B', '#3C1718'], // 6 · terracota (fecha a volta)
];

// 4 tamanhos, e nada além disso. Antes havia 18/20/24/26/28/32/34/40/56/72/74px.
const TAMANHOS = { sm: 24, md: 32, lg: 48, xl: 72 };

// Cada tipo diz forma, nº de iniciais e arte de fundo.
const TIPOS = {
  pessoa:  { coisa: false, letras: 2, arte: 'trabalhador' }, // funcionário, usuário, supervisor
  cliente: { coisa: false, letras: 2, arte: 'busto' },
  obra:    { coisa: true,  letras: 1, arte: 'skyline' },
  empresa: { coisa: true,  letras: 1, arte: null },          // fornecedor, terceirizado, tenant
};

// Artes no viewBox 32x32 — escalam junto com o avatar.
const ARTES = {
  skyline: (fg) => '<g fill="' + fg + '" opacity=".3"><rect x="3" y="20" width="5" height="9"/>'
    + '<rect x="9" y="16" width="6" height="13"/><rect x="16" y="22" width="5" height="7"/>'
    + '<rect x="22" y="18" width="6" height="11"/></g>',
  trabalhador: (fg) => '<g fill="' + fg + '" opacity=".24"><path d="M9 11a7 7 0 0 1 14 0z"/>'
    + '<rect x="6.5" y="11" width="19" height="2.2" rx="1.1"/><path d="M6 30a10 10 0 0 1 20 0z"/></g>',
  busto: (fg) => '<g fill="' + fg + '" opacity=".26"><circle cx="16" cy="12" r="4.6"/>'
    + '<path d="M6 30a10 10 0 0 1 20 0z"/></g>',
};
const DESLOCA_Y = { trabalhador: 2, skyline: -1, busto: 0 };   // a letra foge da arte

let _seq = 0;

/** Cor determinística por chave (SEMPRE o id da entidade). Retorna [bg, fg]. */
export function scAvColor(key) {
  const h = String(key || '').split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return PALETA[h % PALETA.length];
}

/** Iniciais (1–2 letras). */
export function scAvIniciais(nome, letras = 2) {
  return (String(nome || '?').trim().split(/\s+/).slice(0, letras)
    .map((w) => (w[0] || '').toUpperCase()).join('')) || '?';
}

function _esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * scAvatar({ tipo, key, nome, foto, size, badge })
 *   tipo  · 'pessoa' | 'cliente' | 'obra' | 'empresa'
 *   key   · o ID da entidade. NUNCA o nome.
 *   foto  · foto_url / avatar_url. Se existe no banco, PASSE — a arte é só o fallback.
 *   size  · 'sm' 24 · 'md' 32 (padrão) · 'lg' 48 · 'xl' 72
 *   badge · cor do AvatarBadge (status no canto): ex. 'var(--sc-ok)'
 */
export function scAvatar(opts = {}) {
  const { tipo = 'pessoa', key = null, nome = '', foto = null, size = 'md', badge = null } = opts;
  const t = TIPOS[tipo] || TIPOS.pessoa;
  const px = TAMANHOS[size] || TAMANHOS.md;
  const par = scAvColor(key || nome);
  const bg = par[0], fg = par[1];
  const rot = _esc(nome);
  const raio = t.coisa ? 'var(--radius-md)' : '50%';

  let corpo;
  if (foto) {
    // AvatarImage — a arte some assim que a pessoa sobe a foto. É esse o objetivo dela.
    corpo = '<span class="sc-av" data-tipo="' + tipo + '" role="img" aria-label="' + rot + '" title="' + rot + '"'
      + ' style="width:' + px + 'px;height:' + px + 'px;border-radius:' + raio + ';display:inline-block;'
      + 'flex-shrink:0;background:url(\'' + _esc(foto) + '\') center/cover"></span>';
  } else {
    // AvatarFallback — arte + iniciais em SVG (o texto tem contorno e nunca some na arte).
    const cid = 'av' + (++_seq);
    const forma = t.coisa
      ? '<rect width="32" height="32" rx="6.4" fill="' + bg + '"/>'
      : '<circle cx="16" cy="16" r="16" fill="' + bg + '"/>';
    const recorte = t.coisa ? '<rect width="32" height="32" rx="6.4"/>' : '<circle cx="16" cy="16" r="16"/>';
    const arte = t.arte ? ARTES[t.arte](fg) : '';
    const dy = t.arte ? (DESLOCA_Y[t.arte] || 0) : 0;
    const fs = t.coisa ? 14 : 12.6;
    corpo = '<svg class="sc-av" data-tipo="' + tipo + '" width="' + px + '" height="' + px + '" viewBox="0 0 32 32"'
      + ' role="img" aria-label="' + rot + '" style="flex-shrink:0;vertical-align:middle">'
      + '<defs><clipPath id="' + cid + '">' + recorte + '</clipPath></defs>'
      + '<g clip-path="url(#' + cid + ')">' + forma + arte
      + '<text x="16" y="' + (16 + dy) + '" text-anchor="middle" dominant-baseline="central"'
      + ' font-family="Inter, system-ui, sans-serif" font-weight="700" font-size="' + fs + '" fill="' + fg + '"'
      + ' style="paint-order:stroke" stroke="' + bg + '" stroke-width="2.3" stroke-linejoin="round">'
      + _esc(scAvIniciais(nome, t.letras)) + '</text></g></svg>';
  }

  if (!badge) return corpo;
  const dot = Math.max(8, Math.round(px * 0.3));
  return '<span class="sc-av-wrap" style="position:relative;display:inline-flex">' + corpo
    + '<span class="sc-av-badge" style="position:absolute;right:0;bottom:0;width:' + dot + 'px;height:' + dot + 'px;'
    + 'border-radius:50%;background:' + badge + ';border:2px solid var(--card)"></span></span>';
}

/**
 * Pilha sobreposta — AvatarGroup + AvatarGroupCount do shadcn.
 * `total` = quantos existem de verdade (o dashboard mostra 3 avatares de 8 supervisores);
 * sem isso o "+N" contaria só o que veio na lista.
 */
export function scAvatarGroup(itens, opts) {
  const lista = itens || [];
  const o = opts || {};
  const max = o.max || 3;
  const size = o.size || 'md';
  const px = TAMANHOS[size] || TAMANHOS.md;
  const mostra = lista.slice(0, max);
  const resto = Math.max(0, (o.total == null ? lista.length : o.total) - mostra.length);
  const empilha = 'margin-left:-8px;box-shadow:0 0 0 2px var(--card);border-radius:50%';

  const avs = mostra.map((it, i) => '<span style="display:inline-flex;' + (i ? empilha : '') + '">'
    + scAvatar(Object.assign({}, it, { size: size })) + '</span>').join('');

  const conta = resto > 0
    ? '<span class="sc-av-count" style="width:' + px + 'px;height:' + px + 'px;display:inline-grid;'
      + 'place-items:center;background:var(--muted);color:var(--muted-foreground);'
      + 'font:600 ' + Math.max(9, Math.round(px * 0.34)) + 'px/1 inherit;' + empilha + '">+' + resto + '</span>'
    : '';

  return '<span class="sc-av-group" style="display:inline-flex;align-items:center">' + avs + conta + '</span>';
}
